import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useErrorToast, useSuccessToast } from '@enechain/eposition-ui'
import { downloadCSVFromString } from '@enechain/eposition-utils'

import { ContractListFilterState } from '~/pages/ContractList/hooks/useContractListFilter'
import {
  type ContractDealReportModel,
  getExportPeriod,
} from '~/pages/ContractList/models/contractDealReport'
import { ContractCsvRow } from '~/pages/ContractList/types/types'
import { generateAllContractCsvString } from '~/pages/ContractList/utils/allContractCsvGenerator'
import { generateContractExportFileName } from '~/pages/ContractList/utils/contractExportFileNameGenerator'

type UseAllContractCsvExportReturn = {
  exportCsv: () => Promise<void>
  isLoading: boolean
}

export function useAllContractCsvExport(
  data: ContractDealReportModel[] | undefined,
  filterState: ContractListFilterState,
): UseAllContractCsvExportReturn {
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation()

  const { showToast: showSuccessToast } = useSuccessToast({
    toastId: 'AllContractCsvExport/success',
  })
  const { showToast: showErrorToast } = useErrorToast({
    toastId: 'AllContractCsvExport/error',
  })

  async function exportCsv(): Promise<void> {
    setIsLoading(true)

    try {
      if (data == null || data.length === 0) {
        throw new Error('No data to export')
      }

      const { fromDate, toDate } = getExportPeriod(data, filterState)
      const filename = generateContractExportFileName({
        fromDate,
        toDate,
        type: 'ALL',
      })
      const headerLabels = createCsvHeaderLabels(t)
      const csvContent = generateAllContractCsvString(data, headerLabels)

      downloadCSVFromString(csvContent, filename, true)

      showSuccessToast({
        toastMessage: t('common:success.download'),
      })
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'CSV export failed'

      showErrorToast({
        toastMessage: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    exportCsv,
    isLoading,
  }
}

function createCsvHeaderLabels(
  t: (key: string) => string,
): Record<keyof ContractCsvRow, string> {
  return {
    dealtDate: t('page:contract_list.table.headers.execution_date'),
    platformName: t('page:contract_list.table.headers.execution_route'),
    eclearDealCode: t('page:contract_list.table.headers.deal_id'),
    dealPosition: t('page:contract_list.table.headers.side'),
    areaCode: t('page:contract_list.table.headers.area'),
    settlementDifferenceTargetArea: t(
      'domain:shared.settlement_difference_target_area.title',
    ),
    deliveryStartDate: t('page:contract_list.table.headers.delivery_period'),
    deliveryEndDate: t('page:contract_list.table.headers.delivery_period'),
    deliveryTypeString: t('page:contract_list.table.headers.product_type'),
    deliveryHourType: t('page:contract_list.table.headers.time_band'),
    volumeInKw: t('page:contract_list.table.headers.quantity_kw'),
    totalDeliveryHour: t('page:contract_list.table.headers.delivery_hours'),
    totalKwh: t('page:contract_list.table.headers.energy_kwh'),
    unitPricePerKwh: t('page:contract_list.table.headers.contract_price'),
    floatingIndexFormula: t('page:contract_list.table.headers.fuel_formula'),
    currentMarketPrice: t('page:contract_list.table.headers.current_price'),
    mtm: t('page:contract_list.table.headers.mtm_amount'),
    clearingFeeRate: t('page:contract_list.table.headers.clearing_fee_unit'),
    fee: t('page:contract_list.table.headers.clearing_fee_total'),
  }
}
